'use client'

import { useMemo } from 'react'
import useWebSocket, { type Options, ReadyState } from 'react-use-websocket'

import { getWebSocketUrl } from '@/lib/utils/ws-util'

// ============================================
// Base WebSocket Hook (Low-level)
// ============================================

interface BaseProps {
  options?: Options
  connected?: boolean
}

const defaultOptions: Options = {
  shouldReconnect: (closeEvent) => {
    if (closeEvent?.code === 1000) return false
    return closeEvent?.code !== 1000
  },
  reconnectAttempts: 5,
  reconnectInterval: 5000,
  filter: (message) => {
    const data = message?.data
    if (data instanceof ArrayBuffer) {
      return true
    } else if (data instanceof Blob) {
      return true
    } else {
      return true
    }
  },
}

export default function useBaseWebSocket({ options = {}, connected = false }: BaseProps = {}) {
  const getUrl = useMemo(() => getWebSocketUrl, [])

  const { sendMessage, lastMessage, readyState, getWebSocket } = useWebSocket(
    getUrl,
    {
      ...defaultOptions,
      ...options,
    },
    connected
  )

  const connectionStatus = {
    [ReadyState.CONNECTING]: 'CONNECTING' as const,
    [ReadyState.OPEN]: 'OPEN' as const,
    [ReadyState.CLOSING]: 'CLOSING' as const,
    [ReadyState.CLOSED]: 'CLOSED' as const,
    [ReadyState.UNINSTANTIATED]: 'UNINSTANTIATED' as const,
  }[readyState]

  const sendProto = (message: Uint8Array<ArrayBuffer>) => {
    sendMessage(message, true)
  }

  const close = () => {
    const ws = getWebSocket()
    ws?.close()
  }

  return {
    sendProto,
    close,
    lastMessage,
    readyState,
    connectionStatus,
  }
}
